import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FaArrowLeft, FaLink, FaUsers, FaChartLine, FaCopy, FaCheck, FaFileExport, FaCalendarAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import ReferralService from '../../../service/admin/referralService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { useLoader } from '../../../base/context/LoaderProvider';
import ExcelJS from 'exceljs';
import moment from 'moment';
import './detail.css';

const isSuccess = (res) => res?.status?.code === 200;
const getData = (res) => res?.data;

const ITEMS_PER_PAGE = 20;

// Export Progress Component
const ExportProgress = ({ progress, isExporting, status, onClose, activeTab }) => {
  if (!isExporting) return null;

  const getStatusIcon = () => {
    switch (status) {
      case 'fetching':
        return <FaSpinner className="ep-icon ep-spinning" />;
      case 'processing':
        return <FaSpinner className="ep-icon ep-spinning" />;
      case 'downloading':
        return <FaSpinner className="ep-icon ep-spinning" />;
      case 'completed':
        return <FaCheck className="ep-icon ep-completed" />;
      case 'error':
        return <FaTimes className="ep-icon ep-error" />;
      default:
        return <FaSpinner className="ep-icon ep-spinning" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'fetching':
        return `Fetching ${activeTab === 'signups' ? 'signups' : 'visits'}...`;
      case 'processing':
        return 'Processing data...';
      case 'downloading':
        return 'Generating file...';
      case 'completed':
        return 'Export completed!';
      case 'error':
        return 'Export failed';
      default:
        return 'Exporting...';
    }
  };

  return (
    <div className="export-progress-container">
      <div className="export-progress-card">
        <div className="export-progress-header">
          <div className="export-progress-title-section">
            {getStatusIcon()}
            <span className="export-progress-title">{activeTab === 'signups' ? 'Signups' : 'Visits'} Export</span>
          </div>
          {status === 'completed' || status === 'error' ? (
            <button
              className="export-progress-close"
              onClick={onClose}
              title="Close"
            >
              ✕
            </button>
          ) : null}
        </div>

        <div className="export-progress-status">
          <span className="export-progress-step">{getStatusText()}</span>
        </div>

        <div className="export-progress-bar-wrapper">
          <div className="export-progress-bar">
            <div
              className="export-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="export-progress-percentage">
          {progress}%
        </div>
      </div>
    </div>
  );
};

export default function ReferralDetail() {
  const { id } = useParams();
  const message = useMessage();
  const loader = useLoader();
  const exportButtonRef = React.useRef(null);

  const [code, setCode] = useState(null);
  const [visits, setVisits] = useState([]);
  const [signups, setSignups] = useState([]);
  const [activeTab, setActiveTab] = useState('visits');
  const [copied, setCopied] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({ startDate: "", endDate: "" });
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState('fetching');
  const [showExportProgress, setShowExportProgress] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Pagination states
  const [visitsPage, setVisitsPage] = useState(1);
  const [signupsPage, setSignupsPage] = useState(1);
  const [visitsPagination, setVisitsPagination] = useState({ total: 0, totalPages: 0 });
  const [signupsPagination, setSignupsPagination] = useState({ total: 0, totalPages: 0 });
  const [signupsLoaded, setSignupsLoaded] = useState(false);

  const fetchVisitsData = useCallback(async (page = 1) => {
    try {
      const visitsRes = await ReferralService.getVisits(id, { 
        limit: ITEMS_PER_PAGE, 
        page: page 
      });

      if (isSuccess(visitsRes)) {
        const visitsData = getData(visitsRes);
        setVisits(visitsData.visits || []);
        setVisitsPagination({
          total: visitsData.pagination?.total || 0,
          totalPages: visitsData.pagination?.totalPages || 0
        });
      }
    } catch (err) {
      message.showToast('Failed to fetch visits', ToastTypes.ERROR);
    }
  }, [id, message]);

  const fetchSignupsData = useCallback(async (page = 1) => {
    loader.showLoading(true);
    try {
      const signupsRes = await ReferralService.getSignups(id, { 
        limit: ITEMS_PER_PAGE, 
        page: page 
      });

      if (isSuccess(signupsRes)) {
        const signupsData = getData(signupsRes);
        setSignups(signupsData.users || []);
        setSignupsPagination({
          total: signupsData.pagination?.total || 0,
          totalPages: signupsData.pagination?.totalPages || 0
        });
        setSignupsLoaded(true);
      }
    } catch (err) {
      message.showToast('Failed to fetch signups', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  }, [id, loader, message]);

  const fetchCodeDetails = useCallback(async () => {
    loader.showLoading(true);
    try {
      const codeRes = await ReferralService.getCodeById(id);

      if (isSuccess(codeRes)) {
        const codeData = getData(codeRes);
        setCode(codeData);
        
        // Fetch visits with pagination
        await fetchVisitsData(1);
      }
    } catch (err) {
      message.showToast('Failed to fetch referral details', ToastTypes.ERROR);
    } finally {
      loader.showLoading(false);
    }
  }, [id, loader, message]);

  useEffect(() => {
    fetchCodeDetails();
  }, [fetchCodeDetails]);

  // Handle tab switching with lazy loading for signups
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    if (tab === 'signups' && !signupsLoaded) {
      fetchSignupsData(1);
    }
  };

  const handleCopyUrl = async () => {
    try {
      const res = await ReferralService.generateUrl(id, 'hash');
      if (isSuccess(res)) {
        await navigator.clipboard.writeText(getData(res).url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        message.showToast('URL copied to clipboard', ToastTypes.SUCCESS);
      }
    } catch (err) {
      message.showToast('Failed to copy URL', ToastTypes.ERROR);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Export functions
  const exportSignupsToExcel = async (signupsData, filename, onProgress = null) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Signups');

      // Define columns with headers
      const columns = [
        { header: 'S.No', key: 'sno', width: 8 },
        { header: 'Name', key: 'name', width: 25 },
        { header: 'Email', key: 'email', width: 30 },
        { header: 'Phone', key: 'phone', width: 15 },
        { header: 'Company Name', key: 'companyName', width: 25 },
        { header: 'Referral Code', key: 'referralCode', width: 15 },
        { header: 'Signed Up At', key: 'createdAt', width: 20 },
        { header: 'Status', key: 'status', width: 12 },
        { header: 'Role', key: 'role', width: 12 },
      ];

      // Set column definitions
      worksheet.columns = columns;

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 20;

      // Add data rows with progress tracking
      const totalSignups = signupsData.length;
      signupsData.forEach((signup, index) => {
        const row = worksheet.addRow({
          sno: index + 1,
          name: `${signup.firstName || ""} ${signup.lastName || ""}`.trim() || "-",
          email: signup.email || "",
          phone: signup.phone || "",
          companyName: signup.companyName || "",
          referralCode: code?.code || "-",
          createdAt: signup.createdAt ? moment(signup.createdAt).format("DD/MM/YYYY HH:mm") : "",
          status: signup.isDisable ? "Disabled" : "Active",
          role: signup.role || "seller",
        });

        // Style data rows
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        // Update progress every 10 items during processing
        if (onProgress && (index + 1) % 10 === 0) {
          const processingProgress = Math.round((index + 1) / totalSignups * 70) + 10; // 10-80% for processing
          onProgress({ progress: processingProgress, status: 'processing' });
        }
      });

      // Auto-fit columns based on content
      columns.forEach((col, index) => {
        const column = worksheet.getColumn(index + 1);
        let maxLength = col.header.length;
        
        // Find max content length in this column
        worksheet.getColumn(col.key).eachCell({ includeEmpty: false }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        
        // Set width with some padding (min 10, max 50)
        column.width = Math.min(50, Math.max(10, maxLength + 2));
      });

      // Update progress to downloading phase
      if (onProgress) {
        onProgress({ progress: 80, status: 'downloading' });
      }

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Complete progress
      if (onProgress) {
        onProgress({ progress: 100, status: 'completed' });
      }
    } catch (error) {
      console.error('Excel export error:', error);
      if (onProgress) {
        onProgress({ progress: 0, status: 'error' });
      }
      throw error;
    }
  };

  const exportVisitsToExcel = async (visitsData, filename, onProgress = null) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Visits');

      // Define columns with headers
      const columns = [
        { header: 'S.No', key: 'sno', width: 8 },
        { header: 'Date', key: 'createdAt', width: 20 },
        { header: 'IP Address', key: 'ip', width: 15 },
        { header: 'User Agent', key: 'userAgent', width: 40 },
        { header: 'UTM Source', key: 'utmSource', width: 15 },
        { header: 'UTM Medium', key: 'utmMedium', width: 15 },
        { header: 'UTM Campaign', key: 'utmCampaign', width: 15 },
        { header: 'Converted', key: 'convertedToSignup', width: 12 },
        { header: 'Referral Code', key: 'referralCode', width: 15 },
      ];

      // Set column definitions
      worksheet.columns = columns;

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 20;

      // Add data rows with progress tracking
      const totalVisits = visitsData.length;
      visitsData.forEach((visit, index) => {
        const row = worksheet.addRow({
          sno: index + 1,
          createdAt: visit.createdAt ? moment(visit.createdAt).format("DD/MM/YYYY HH:mm") : "",
          ip: visit.ip || "-",
          userAgent: visit.userAgent || "-",
          utmSource: visit.utmSource || "-",
          utmMedium: visit.utmMedium || "-",
          utmCampaign: visit.utmCampaign || "-",
          convertedToSignup: visit.convertedToSignup ? "Yes" : "No",
          referralCode: code?.code || "-",
        });

        // Style data rows
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        // Update progress every 10 items during processing
        if (onProgress && (index + 1) % 10 === 0) {
          const processingProgress = Math.round((index + 1) / totalVisits * 70) + 10; // 10-80% for processing
          onProgress({ progress: processingProgress, status: 'processing' });
        }
      });

      // Auto-fit columns based on content
      columns.forEach((col, index) => {
        const column = worksheet.getColumn(index + 1);
        let maxLength = col.header.length;
        
        // Find max content length in this column
        worksheet.getColumn(col.key).eachCell({ includeEmpty: false }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        
        // Set width with some padding (min 10, max 50)
        column.width = Math.min(50, Math.max(10, maxLength + 2));
      });

      // Update progress to downloading phase
      if (onProgress) {
        onProgress({ progress: 80, status: 'downloading' });
      }

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Complete progress
      if (onProgress) {
        onProgress({ progress: 100, status: 'completed' });
      }
    } catch (error) {
      console.error('Excel export error:', error);
      if (onProgress) {
        onProgress({ progress: 0, status: 'error' });
      }
      throw error;
    }
  };

  const handleExportAllSignups = async () => {
    setShowExportModal(false);
    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus('fetching');

    try {
      // Fetch all signups without pagination
      setExportProgress(20);
      const res = await ReferralService.getSignups(id, { limit: 10000 });
      
      if (isSuccess(res)) {
        const allSignups = getData(res).users || [];
        if (allSignups.length === 0) {
          message.showToast("No signups to export", ToastTypes.WARNING);
          setExportProgress(0);
          setExporting(false);
          setShowExportProgress(false);
          return;
        }

        setExportProgress(30);
        setExportStatus('processing');
        
        await exportSignupsToExcel(allSignups, `all_signups_${code?.code}_${Date.now()}`, ({ progress, status }) => {
          setExportProgress(progress);
          setExportStatus(status);
        });
        
        message.showToast(`Exported ${allSignups.length} signups successfully`, ToastTypes.SUCCESS);
      } else {
        message.showToast("Failed to export signups", ToastTypes.ERROR);
        setExportProgress(0);
        setExportStatus('error');
      }
    } catch (err) {
      message.showToast(err?.message || "Error exporting signups", ToastTypes.ERROR);
      setExportProgress(0);
      setExportStatus('error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAllVisits = async () => {
    setShowExportModal(false);
    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus('fetching');

    try {
      // Fetch all visits without pagination
      setExportProgress(20);
      const res = await ReferralService.getVisits(id, { limit: 10000 });
      
      if (isSuccess(res)) {
        const allVisits = getData(res).visits || [];
        if (allVisits.length === 0) {
          message.showToast("No visits to export", ToastTypes.WARNING);
          setExportProgress(0);
          setExporting(false);
          setShowExportProgress(false);
          return;
        }

        setExportProgress(30);
        setExportStatus('processing');
        
        await exportVisitsToExcel(allVisits, `all_visits_${code?.code}_${Date.now()}`, ({ progress, status }) => {
          setExportProgress(progress);
          setExportStatus(status);
        });
        
        message.showToast(`Exported ${allVisits.length} visits successfully`, ToastTypes.SUCCESS);
      } else {
        message.showToast("Failed to export visits", ToastTypes.ERROR);
        setExportProgress(0);
        setExportStatus('error');
      }
    } catch (err) {
      message.showToast(err?.message || "Error exporting visits", ToastTypes.ERROR);
      setExportProgress(0);
      setExportStatus('error');
    } finally {
      setExporting(false);
    }
  };

  const handleExportDateWise = async () => {
    if (!exportDateRange.startDate || !exportDateRange.endDate) {
      message.showToast("Please select start date and end date", ToastTypes.WARNING);
      return;
    }

    setShowExportModal(false);
    setExporting(true);
    setShowExportProgress(true);
    setExportProgress(0);
    setExportStatus('fetching');

    try {
      // Fetch all signups first (we'll filter by date on client side since API might not support date filtering)
      setExportProgress(20);
      const res = await ReferralService.getSignups(id, { limit: 10000 });
      
      if (isSuccess(res)) {
        const allSignups = getData(res).users || [];
        
        // Filter by date range
        const startDate = new Date(exportDateRange.startDate);
        const endDate = new Date(exportDateRange.endDate);
        endDate.setHours(23, 59, 59, 999); // Include end date fully
        
        const dateWiseSignups = allSignups.filter(signup => {
          const signupDate = new Date(signup.createdAt);
          return signupDate >= startDate && signupDate <= endDate;
        });
        
        if (dateWiseSignups.length === 0) {
          message.showToast("No signups found for selected date range", ToastTypes.WARNING);
          setExportProgress(0);
          setExporting(false);
          setShowExportProgress(false);
          return;
        }

        setExportProgress(30);
        setExportStatus('processing');

        const dateStr = `${exportDateRange.startDate}_to_${exportDateRange.endDate}`;
        await exportSignupsToExcel(dateWiseSignups, `signups_${code?.code}_${dateStr}_${Date.now()}`, ({ progress, status }) => {
          setExportProgress(progress);
          setExportStatus(status);
        });
        
        message.showToast(`Exported ${dateWiseSignups.length} signups successfully`, ToastTypes.SUCCESS);
      } else {
        message.showToast("Failed to export signups", ToastTypes.ERROR);
        setExportProgress(0);
        setExportStatus('error');
      }
    } catch (err) {
      message.showToast(err?.message || "Error exporting signups", ToastTypes.ERROR);
      setExportProgress(0);
      setExportStatus('error');
    } finally {
      setExporting(false);
    }
  };

  const handleCloseExportProgress = () => {
    setShowExportProgress(false);
    setExportProgress(0);
    setExportStatus('fetching');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportModal && exportButtonRef.current && !exportButtonRef.current.contains(event.target)) {
        setShowExportModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportModal]);

  // Pagination helpers
  const getPaginatedData = (data, page) => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return data.slice(start, start + ITEMS_PER_PAGE);
  };

  const getTotalPages = (data) => Math.ceil(data.length / ITEMS_PER_PAGE);

  // Handle pagination changes
  const handleVisitsPageChange = (newPage) => {
    setVisitsPage(newPage);
    fetchVisitsData(newPage);
  };

  const handleSignupsPageChange = (newPage) => {
    setSignupsPage(newPage);
    fetchSignupsData(newPage);
  };

  // Remove client-side pagination since we're using server-side pagination
  const paginatedVisits = visits;
  const paginatedSignups = signups;
  const totalVisitsPages = visitsPagination.totalPages;
  const totalSignupsPages = signupsPagination.totalPages;

  if (!code) {
    return (
      <div className="rmd-page">
        <div className="rmd-loading">Loading...</div>
      </div>
    );
  }

  const convRate = (code?.stats?.totalVisits && code?.stats?.totalVisits > 0) 
    ? ((code?.stats?.signups / code?.stats?.totalVisits) * 100).toFixed(1) 
    : '0';

  return (
    <div className="rmd-page">
      <div className="rmd-header">
        <div className="rmd-header-left">
          <button className="rmd-back-btn" onClick={() => window.close()}>
            <FaArrowLeft /> Back
          </button>
          <div className="rmd-header-info">
            <h1 className="rmd-title">{code.name}</h1>
            <span className="rmd-code-badge">{code.code}</span>
            <span className={`rmd-type-badge rmd-type-${code.type}`}>{code.type}</span>
            <span className={`rmd-status-badge ${code.isActive ? 'rmd-status-active' : 'rmd-status-inactive'}`}>
              {code.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <button className="rmd-copy-btn" onClick={handleCopyUrl}>
          {copied ? <FaCheck /> : <FaCopy />} Copy URL
        </button>
      </div>

      {/* Stats */}
      <div className="rmd-stats-grid">
        <div className="rmd-stat-card">
          <div className="rmd-stat-icon rmd-icon-blue"><FaLink /></div>
          <div className="rmd-stat-content">
            <span className="rmd-stat-value">{code?.stats?.totalVisits || 0}</span>
            <span className="rmd-stat-label">TOTAL VISITS</span>
          </div>
        </div>
        <div className="rmd-stat-card">
          <div className="rmd-stat-icon rmd-icon-green"><FaUsers /></div>
          <div className="rmd-stat-content">
            <span className="rmd-stat-value">{code?.stats?.signups || 0}</span>
            <span className="rmd-stat-label">TOTAL SIGNUPS</span>
          </div>
        </div>
        <div className="rmd-stat-card">
          <div className="rmd-stat-icon rmd-icon-purple"><FaChartLine /></div>
          <div className="rmd-stat-content">
            <span className="rmd-stat-value">
              {(code?.stats?.totalVisits && code?.stats?.totalVisits > 0) 
                ? ((code?.stats?.signups / code?.stats?.totalVisits) * 100).toFixed(1) 
                : '0'}%
            </span>
            <span className="rmd-stat-label">CONVERSION RATE</span>
          </div>
        </div>
      </div>

      {/* Description */}
      {code.description && (
        <div className="rmd-description">
          <strong>Description:</strong> {code.description}
        </div>
      )}

      {/* Tabs */}
      <div className="rmd-tabs">
        <button
          className={`rmd-tab-btn ${activeTab === 'visits' ? 'rmd-tab-active' : ''}`}
          onClick={() => handleTabSwitch('visits')}
        >
          Visits ({code?.stats?.totalVisits || 0})
        </button>
        <button
          className={`rmd-tab-btn ${activeTab === 'signups' ? 'rmd-tab-active' : ''}`}
          onClick={() => handleTabSwitch('signups')}
        >
          Signups ({code?.stats?.signups || 0})
        </button>
        <div className="rmd-export-dropdown-container" ref={exportButtonRef}>
          <button
            className="ref-management-export-btn"
            onClick={() => setShowExportModal(!showExportModal)}
            title={`Export ${activeTab === 'signups' ? 'Signups' : 'Visits'}`}
          >
            <FaFileExport /> Export
          </button>
          
          {/* Export Dropdown */}
          {showExportModal && (
            <div className="ref-management-export-dropdown">
              <div className="ref-management-export-dropdown-content">
                {activeTab === 'signups' ? (
                  <>
                    <div className="ref-management-export-dropdown-section">
                      <h4>Date Range Export</h4>
                      <div className="ref-management-export-date-inputs">
                        <div className="ref-management-date-input-group">
                          <label>From:</label>
                          <input
                            type="date"
                            value={exportDateRange.startDate}
                            onChange={(e) => setExportDateRange({ ...exportDateRange, startDate: e.target.value })}
                          />
                        </div>
                        <div className="ref-management-date-input-group">
                          <label>To:</label>
                          <input
                            type="date"
                            value={exportDateRange.endDate}
                            onChange={(e) => setExportDateRange({ ...exportDateRange, endDate: e.target.value })}
                          />
                        </div>
                      </div>
                      <button
                        className="ref-management-export-btn ref-management-export-btn-date"
                        onClick={handleExportDateWise}
                        disabled={exporting || !exportDateRange.startDate || !exportDateRange.endDate}
                      >
                        <FaCalendarAlt /> Export Date Wise
                      </button>
                    </div>

                    <div className="ref-management-export-dropdown-divider">
                      <span>OR</span>
                    </div>
                  </>
                ) : null}

                <div className="ref-management-export-dropdown-section">
                  <h4>Export All {activeTab === 'signups' ? 'Signups' : 'Visits'}</h4>
                  <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '10px' }}>
                    Export all {activeTab === 'signups' ? 'signup' : 'visit'} data for this referral code
                  </p>
                  <button
                    className="ref-management-export-btn ref-management-export-btn-all"
                    onClick={activeTab === 'signups' ? handleExportAllSignups : handleExportAllVisits}
                    disabled={exporting}
                  >
                    <FaFileExport /> Export All {activeTab === 'signups' ? 'Signups' : 'Visits'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Visits Tab */}
      {activeTab === 'visits' && (
        <>
          <div className="rmd-table-container">
            <table className="rmd-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>DATE</th>
                  <th>IP ADDRESS</th>
                  <th>USER AGENT</th>
                  <th>UTM SOURCE</th>
                  <th>UTM MEDIUM</th>
                  <th>UTM CAMPAIGN</th>
                  <th>CONVERTED</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVisits.length === 0 ? (
                  <tr><td colSpan="8" className="rmd-no-data">No visits recorded</td></tr>
                ) : (
                  paginatedVisits.map((visit, idx) => (
                    <tr key={visit._id}>
                      <td>{(visitsPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                      <td>{formatDate(visit.createdAt)}</td>
                      <td>{visit.ip || '-'}</td>
                      <td className="rmd-ua-cell" title={visit.userAgent}>{visit.userAgent?.substring(0, 50) || '-'}...</td>
                      <td>{visit.utmSource || '-'}</td>
                      <td>{visit.utmMedium || '-'}</td>
                      <td>{visit.utmCampaign || '-'}</td>
                      <td>
                        {visit.convertedToSignup ? (
                          <span className="rmd-converted-yes">Yes</span>
                        ) : (
                          <span className="rmd-converted-no">No</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalVisitsPages > 1 && (
            <div className="rmd-pagination">
              <button 
                className="rmd-page-btn"
                disabled={visitsPage <= 1} 
                onClick={() => handleVisitsPageChange(visitsPage - 1)}
              >
                Previous
              </button>
              <span className="rmd-page-info">Page {visitsPage} of {totalVisitsPages}</span>
              <button 
                className="rmd-page-btn"
                disabled={visitsPage >= totalVisitsPages} 
                onClick={() => handleVisitsPageChange(visitsPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Signups Tab */}
      {activeTab === 'signups' && (
        <>
          <div className="rmd-table-container">
            <table className="rmd-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>NAME</th>
                  <th>EMAIL</th>
                  <th>PHONE</th>
                  <th>COMPANY</th>
                  <th>SIGNED UP</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSignups.length === 0 ? (
                  <tr><td colSpan="6" className="rmd-no-data">No signups yet</td></tr>
                ) : (
                  paginatedSignups.map((user, idx) => (
                    <tr key={user._id}>
                      <td>{(signupsPage - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.phone || '-'}</td>
                      <td>{user.companyName || '-'}</td>
                      <td>{formatDate(user.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalSignupsPages > 1 && (
            <div className="rmd-pagination">
              <button 
                className="rmd-page-btn"
                disabled={signupsPage <= 1} 
                onClick={() => handleSignupsPageChange(signupsPage - 1)}
              >
                Previous
              </button>
              <span className="rmd-page-info">Page {signupsPage} of {totalSignupsPages}</span>
              <button 
                className="rmd-page-btn"
                disabled={signupsPage >= totalSignupsPages} 
                onClick={() => handleSignupsPageChange(signupsPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Meta Info */}
      <div className="rmd-meta">
        <span>Created: {formatDate(code.createdAt)}</span>
        {code.expiresAt && <span>Expires: {formatDate(code.expiresAt)}</span>}
        {code.commissionPercent > 0 && <span>Commission: {code.commissionPercent}%</span>}
      </div>

      {/* Export Progress */}
      <ExportProgress
        progress={exportProgress}
        isExporting={showExportProgress}
        status={exportStatus}
        onClose={handleCloseExportProgress}
        activeTab={activeTab}
      />
    </div>
  );
}
